/**
 * Text pasted/typed by users (often copied from PDFs, Word, or invoice
 * viewers) can silently contain characters that are visually identical to
 * normal ASCII but are NOT what strict validators (e.g. `@IsAlphanumeric`)
 * expect, causing confusing "must have only alphanumeric characters" errors
 * even when the text looks correct to the human eye. This normalizes those
 * invisible/lookalike characters back to their plain ASCII equivalents:
 *
 * 1. Invisible whitespace/zero-width characters (common when copy-pasting
 *    from PDFs) — non-breaking space, zero-width space/joiner/non-joiner,
 *    word joiner, BOM — are converted to a regular space (or removed).
 * 2. Greek/Cyrillic letters that are visual homoglyphs of Latin letters
 *    (an OCR/vision-model artifact, e.g. Greek "Μ" instead of Latin "M")
 *    are mapped back to their Latin lookalike.
 */

const INVISIBLE_TO_SPACE_REGEX = /[\u00A0\u2007\u202F]/g;
const INVISIBLE_TO_REMOVE_REGEX = /[\u200B\u200C\u200D\u2060\uFEFF]/g;

const HOMOGLYPH_MAP: Record<string, string> = {
  // Greek uppercase
  'Α': 'A', 'Β': 'B', 'Ε': 'E', 'Ζ': 'Z', 'Η': 'H', 'Ι': 'I', 'Κ': 'K',
  'Μ': 'M', 'Ν': 'N', 'Ο': 'O', 'Ρ': 'P', 'Τ': 'T', 'Υ': 'Y', 'Χ': 'X',
  // Greek lowercase
  'α': 'a', 'ο': 'o', 'ν': 'v', 'υ': 'u', 'ι': 'i',
  // Cyrillic uppercase
  'А': 'A', 'В': 'B', 'Е': 'E', 'Ё': 'E', 'К': 'K', 'М': 'M', 'Н': 'H',
  'О': 'O', 'Р': 'P', 'С': 'C', 'Т': 'T', 'У': 'Y', 'Х': 'X',
  // Cyrillic lowercase
  'а': 'a', 'е': 'e', 'ѕ': 's', 'і': 'i', 'ј': 'j', 'о': 'o', 'р': 'p',
  'с': 'c', 'у': 'y', 'х': 'x',
};

export function sanitizeTextInput(value: string): string {
  if (typeof value !== 'string' || !value) return value;

  let out = value
    .replace(INVISIBLE_TO_SPACE_REGEX, ' ')
    .replace(INVISIBLE_TO_REMOVE_REGEX, '');

  let mapped = '';
  for (const ch of out) {
    mapped += HOMOGLYPH_MAP[ch] ?? ch;
  }

  return mapped;
}
